import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject,
  inject,
  input,
  OnDestroy, OnInit,
  Output,
  PLATFORM_ID,
  signal
} from '@angular/core';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  filter,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';

import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  ADMIN_DEFAULT_IN_PERSON_RADIUS,
  DEFAULT_IN_PERSON_RADIUS,
  FilterOptionsStore,
  InPersonRadius,
  IFilterForm,
  IFilterFormOptionValue,
  IFilterFormValues,
  ILatLon,
  LGBT_IDENTIFIED_OPTION,
  PSYCHIATRIST_URL_TITLE,
} from '@sondermind/data-access/public-facing/filter-form';
import { GoogleMapsGeocodeService } from '@sondermind/google-maps';
import { EmptyURIValue } from '@sondermind/data-access/directory-link';
import { IInsuranceOptionValue } from '@sondermind/utilities/models-provider-network-company';
import { BreakpointsService } from '@sondermind/utilities/services';
import { ProviderNameSearchComponent, SearchLayout } from '@sondermind/features/provider-name-search';
import { IFilterCategoryConfig } from '../../models/filter-category-config.interface';
import { FilterCategory } from '../../models/filter-name.constants';
import { IOptionCategory } from '../../models/option-category.interface';
import { FilterLoadingService, IFacetFilterOptions } from '../../services/filter-loading.service';
import { FilterParseService } from '../../services/filter-parse.service';
import { FilterValidationService } from '../../services/filter-validation.service';
import { isInsuranceInOptions } from '../../utils/insurance-selection.util';
import { FilterSelectMobileComponent } from '../filter-select-mobile/filter-select-mobile.component';
import { FilterSelectComponent } from '../filter-select/filter-select.component';
import { MoreFiltersComponent } from '../more-filters/more-filters.component';
import { InPersonSearchStore } from '../../stores/in-person-search.store';

/** Added by addAdminOnlyControls() only on the admin directory; all render through the multi-select. */
const ADMIN_ONLY_CONTROLS = ['licensures', 'ethnicities', 'lgbtIdentified'] as const;

const LGBT_IDENTIFIED_OPTIONS: Array<IOptionCategory<IFilterFormOptionValue>> = [{
  name: FilterCategory.LGBT_IDENTIFIED,
  options: [{ value: LGBT_IDENTIFIED_OPTION, name: LGBT_IDENTIFIED_OPTION.title }]
}];

@Component({
  selector: 'filter-form',
  standalone: true,
  imports: [
    CommonModule,
    FilterSelectComponent,
    MoreFiltersComponent,
    FilterSelectMobileComponent,
    ReactiveFormsModule,
    ProviderNameSearchComponent,
  ],
  providers: [InPersonSearchStore],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './filter-form.component.html',
  styleUrl: './filter-form.component.scss'
})
export class FilterFormComponent implements OnInit, OnDestroy {
  readonly internalDirectory = input<boolean>(false);

  /**
   * Care Coordination's directory in the admin portal, which gets filters the public directory must
   * not offer. Distinct from `internalDirectory`, which only affects provider-name search navigation.
   */
  readonly adminDirectory = input<boolean>(false);

  @Output() filterRetrievalError = new EventEmitter<boolean>();
  @Output() redirectToNotFound = new EventEmitter<boolean>();
  @Output() formFiltersChanged = new EventEmitter<Partial<IFilterFormValues>>();
  stateOptions: Array<IOptionCategory<string>> = [];
  insuranceOptions: Array<IOptionCategory<IInsuranceOptionValue>> = [];
  specialtyOptions: Array<IOptionCategory<IFilterFormOptionValue>> = [];
  modalityOptions: Array<IOptionCategory<IFilterFormOptionValue>> = [];
  genderOptions: Array<IOptionCategory<IFilterFormOptionValue>> = [];
  sessionFormatOptions: Array<IOptionCategory<IFilterFormOptionValue>> = [];
  ageOptions: Array<IOptionCategory<IFilterFormOptionValue>> = [];
  treatmentPreferenceOptions: Array<IOptionCategory<IFilterFormOptionValue>> = [];
  languageOptions: Array<IOptionCategory<IFilterFormOptionValue>> = [];
  serviceTypeOptions: Array<IOptionCategory<IFilterFormOptionValue>> = [];
  /** Facet-backed admin-only option lists by category; empty off the admin directory. */
  private facetOptions = new Map<FilterCategory, Array<IOptionCategory<IFilterFormOptionValue>>>();

  filterFormGroup: FormGroup<IFilterForm> = new FormGroup({
    city: new FormControl<string | null>(null, Validators.required),
    state: new FormControl<string | null>(null, Validators.required),
    serviceType: new FormControl<IFilterFormOptionValue | null>(null, Validators.required),
    insurance: new FormControl<IInsuranceOptionValue | null>(null),
    specialties: new FormControl<IFilterFormOptionValue[]>([], { nonNullable: true }),
    modalities: new FormControl<IFilterFormOptionValue[]>([], { nonNullable: true }),
    genders: new FormControl<IFilterFormOptionValue[]>([], { nonNullable: true }),
    sessionFormats: new FormControl<IFilterFormOptionValue[]>([], { nonNullable: true }),
    ages: new FormControl<IFilterFormOptionValue[]>([], { nonNullable: true }),
    treatmentPreferences: new FormControl<IFilterFormOptionValue[]>([], { nonNullable: true }),
    languages: new FormControl<IFilterFormOptionValue[]>([], { nonNullable: true }),
    // Admin-only controls are added by addAdminOnlyControls(), so the public form never carries them.
    userLatLon: new FormControl<ILatLon | null>(null),
    userZip: new FormControl<string | null>(null),
    userRadius: new FormControl<InPersonRadius>(DEFAULT_IN_PERSON_RADIUS, { nonNullable: true }),
  });
  // The desktop main bar (state, service type, insurance, concerns) binds its selects directly in the template.
  /** Inside the desktop More filters dropdown. */
  desktopMoreFilters: IFilterCategoryConfig[] = [];
  /** The mobile main bar: Concerns alone. */
  mobileMainFilters: IFilterCategoryConfig[] = [];
  /** Inside the mobile More filters modal, including the single-selects the desktop bar shows. */
  mobileMoreFilters: IFilterCategoryConfig[] = [];

  readonly filtersLoading = signal<boolean>(true);
  readonly isMobile = signal<boolean>(false);
  readonly isSearchPanelOpen = signal<boolean>(false);

  protected readonly filterCategory = FilterCategory;
  protected readonly searchLayout = SearchLayout;
  isUpdatingForm: boolean = false;
  private destroyed$ = new Subject<void>();
  private readonly facetStateRequested = new Subject<string | null>();

  readonly inPersonSearchStore = inject(InPersonSearchStore);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  constructor(
    private filterLoadingService: FilterLoadingService,
    private filterValidationService: FilterValidationService,
    private filtersStore: FilterOptionsStore,
    private filterParseService: FilterParseService,
    private breakpointService: BreakpointsService,
    private geocodeService: GoogleMapsGeocodeService,
    @Inject(PLATFORM_ID) private platformId: object,
    private route: ActivatedRoute,
  ) { }

  ngOnInit(): void {
    this.filtersLoading.set(true);

    this.inPersonSearchStore.initializeControls({
      sessionFormats: this.filterFormGroup.controls.sessionFormats,
      userZip: this.filterFormGroup.controls.userZip,
      userLatLon: this.filterFormGroup.controls.userLatLon,
      userRadius: this.filterFormGroup.controls.userRadius,
    });
    this.inPersonSearchStore.setAdminDirectory(this.adminDirectory());
    this.applyAdminRadiusDefault();

    this.subscribeToFormChanges();
    this.subscribeToFacetOptions();
    this.subscribeToFilterLoadingError();
    this.subscribeToFilterValidation();
    this.subscribeToBreakpointChanges();
    this.subscribeToZipChanges();
    this.loadAllFilters();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private loadAllFilters(): void {
    this.filterLoadingService.getAllFilterOptions$().pipe(
      take(1),
      filter((filterOptions) => !!filterOptions),
      tap((filterOptions) => {
        this.stateOptions = filterOptions.stateOptions;
        this.insuranceOptions = filterOptions.insuranceOptions;
        this.specialtyOptions = filterOptions.specialtyOptions;
        this.modalityOptions = filterOptions.modalityOptions;
        this.genderOptions = filterOptions.genderOptions;
        this.sessionFormatOptions = filterOptions.sessionFormatOptions;
        this.ageOptions = filterOptions.ageOptions;
        this.treatmentPreferenceOptions = filterOptions.treatmentPreferenceOptions;
        this.languageOptions = filterOptions.languageOptions;
        this.serviceTypeOptions = filterOptions.serviceTypeOptions;
      }),
      switchMap(() => this.populateFormFromUrl$())
    ).subscribe(() => {
      if (isPlatformBrowser(this.platformId)) {
        this.filtersLoading.set(false);
      }
      this.setupFilterConfigurations();
      this.filtersStore.setIsLoaded(true);
      this.filterFormGroup.updateValueAndValidity();
      // Requested, not awaited: facet round-trips never gate this load. No-op off the admin directory.
      this.facetStateRequested.next(this.filterFormGroup.controls.state.value);
    });
  }

  private populateFormFromUrl$(): Observable<string | null> {
    // Keeps subscribeToFormChanges from resetting city during URL parsing.
    this.isUpdatingForm = true;

    return this.filterParseService.parseAndPopulateV2Form(
      this.route,
      this.filterFormGroup,
      {
        stateOptions: this.stateOptions,
        insuranceOptions: this.insuranceOptions,
        specialtyOptions: this.specialtyOptions,
        modalityOptions: this.modalityOptions,
        genderOptions: this.genderOptions,
        sessionFormatOptions: this.sessionFormatOptions,
        ageOptions: this.ageOptions,
        treatmentPreferenceOptions: this.treatmentPreferenceOptions,
        languageOptions: this.languageOptions,
        serviceTypeOptions: this.serviceTypeOptions
      }
    ).pipe(
      tap(() => {
        this.isUpdatingForm = false;
      }),
      map(() => this.filterFormGroup.controls.state.value)
    );
  }

  private setupFilterConfigurations(): void {
    this.addAdminOnlyControls();
    this.setupDesktopFilters();
    this.setupMobileFilters();
    this.appendAdminOnlyFilters();
  }

  /** Admin starts at the widest radius; silent because it is an initial value, not a user change. */
  private applyAdminRadiusDefault(): void {
    if (!this.adminDirectory()) return;

    this.filterFormGroup.controls.userRadius.setValue(
      ADMIN_DEFAULT_IN_PERSON_RADIUS, { emitEvent: false }
    );
  }

  /** Added at runtime so no other consumer of IFilterForm has to declare them. */
  private addAdminOnlyControls(): void {
    if (!this.adminDirectory()) return;

    ADMIN_ONLY_CONTROLS.forEach((name) => {
      if (this.filterFormGroup.contains(name)) return;

      this.filterFormGroup.addControl(
        name,
        new FormControl<IFilterFormOptionValue[]>([], { nonNullable: true })
      );
    });
  }

  /**
   * Appends the admin-only filters to every More filters list. Options start empty and are filled by
   * the facet load, which republishes these configs rather than mutating them (see publishFacetOptions).
   */
  private appendAdminOnlyFilters(): void {
    const { controls } = this.filterFormGroup;
    const { licensures, ethnicities, lgbtIdentified } = controls;
    if (!this.adminDirectory() || !licensures || !ethnicities || !lgbtIdentified) return;

    const configs: IFilterCategoryConfig[] = [
      {
        category: FilterCategory.LICENSURE,
        options: this.optionsFor(FilterCategory.LICENSURE),
        formControl: licensures,
        isSearchable: true,
        isMulti: true
      },
      {
        category: FilterCategory.ETHNICITY,
        options: this.optionsFor(FilterCategory.ETHNICITY),
        formControl: ethnicities,
        isSearchable: true,
        isMulti: true
      },
      {
        // isMulti kept true so the one option reuses the multi-select renderer.
        category: FilterCategory.LGBT_IDENTIFIED,
        options: LGBT_IDENTIFIED_OPTIONS,
        formControl: lgbtIdentified,
        isSearchable: false,
        isMulti: true
      }
    ];

    this.desktopMoreFilters = [...this.desktopMoreFilters, ...configs];
    this.mobileMoreFilters = [...this.mobileMoreFilters, ...configs];
  }

  /**
   * Watches the state control, not subscribeToFormChanges' state branch: that branch is skipped when
   * the store already holds the state, and it gates the results emit.
   */
  private subscribeToFacetOptions(): void {
    this.facetStateRequested.pipe(
      distinctUntilChanged(),
      filter((state): state is string => this.adminDirectory() && !!state),
      switchMap((state) => this.filterLoadingService.getFacetOptions$(state)),
      takeUntil(this.destroyed$)
    ).subscribe((loaded) => this.publishFacetOptions(loaded));

    this.filterFormGroup.controls.state.valueChanges.pipe(
      takeUntil(this.destroyed$)
    ).subscribe((state) => this.facetStateRequested.next(state));
  }

  private optionsFor(category: FilterCategory): Array<IOptionCategory<IFilterFormOptionValue>> {
    return this.facetOptions.get(category) ?? [];
  }

  /**
   * New arrays, not an in-place fill: more-filters is OnPush and derives its list from its input.
   * markForCheck because an HTTP callback does not mark an OnPush component dirty.
   */
  private publishFacetOptions({ category, options }: IFacetFilterOptions): void {
    this.facetOptions.set(category, options);

    this.desktopMoreFilters = this.withFacetOptions(this.desktopMoreFilters, category);
    this.mobileMoreFilters = this.withFacetOptions(this.mobileMoreFilters, category);

    this.changeDetectorRef.markForCheck();
  }

  private withFacetOptions(
    configs: IFilterCategoryConfig[],
    category: FilterCategory
  ): IFilterCategoryConfig[] {
    return configs.map((config) => (
      config.category === category ? { ...config, options: this.optionsFor(category) } : config
    ));
  }

  private setupDesktopFilters(): void {
    this.desktopMoreFilters = [
      {
        category: FilterCategory.GENDER_PREFERENCE,
        options: this.genderOptions,
        formControl: this.filterFormGroup.controls.genders,
        isSearchable: false,
        isMulti: true
      },
      {
        category: FilterCategory.TREATMENT_APPROACH,
        options: this.treatmentPreferenceOptions,
        formControl: this.filterFormGroup.controls.treatmentPreferences,
        isSearchable: true,
        isMulti: true
      },
      {
        category: FilterCategory.TYPE_OF_SERVICE,
        options: this.modalityOptions,
        formControl: this.filterFormGroup.controls.modalities,
        isSearchable: false,
        isMulti: true
      },
      {
        category: FilterCategory.SESSION_FORMAT,
        options: this.sessionFormatOptions,
        formControl: this.filterFormGroup.controls.sessionFormats,
        isSearchable: false,
        isMulti: true
      },
      {
        category: FilterCategory.AGE_GROUP_TREATED,
        options: this.ageOptions,
        formControl: this.filterFormGroup.controls.ages,
        isSearchable: false,
        isMulti: true
      },
      {
        category: FilterCategory.LANGUAGE,
        options: this.languageOptions,
        formControl: this.filterFormGroup.controls.languages,
        isSearchable: true,
        isMulti: true
      }
    ];
  }

  private setupMobileFilters(): void {
    this.mobileMainFilters = [
      {
        category: FilterCategory.SPECIALTY,
        options: this.specialtyOptions,
        formControl: this.filterFormGroup.controls.specialties,
        isSearchable: true,
        isMulti: true
      }
    ];

    this.mobileMoreFilters = [
      {
        category: FilterCategory.THERAPY_OR_PSYCHIATRY,
        options: this.serviceTypeOptions,
        formControl: this.filterFormGroup.controls.serviceType,
        isSearchable: false,
        isMulti: false
      },
      {
        category: FilterCategory.INSURANCE,
        options: this.insuranceOptions,
        formControl: this.filterFormGroup.controls.insurance,
        isSearchable: true,
        isMulti: false
      },
      {
        category: FilterCategory.GENDER_PREFERENCE,
        options: this.genderOptions,
        formControl: this.filterFormGroup.controls.genders,
        isSearchable: false,
        isMulti: true
      },
      {
        category: FilterCategory.TREATMENT_APPROACH,
        options: this.treatmentPreferenceOptions,
        formControl: this.filterFormGroup.controls.treatmentPreferences,
        isSearchable: true,
        isMulti: true
      },
      {
        category: FilterCategory.TYPE_OF_SERVICE,
        options: this.modalityOptions,
        formControl: this.filterFormGroup.controls.modalities,
        isSearchable: false,
        isMulti: true
      },
      {
        category: FilterCategory.SESSION_FORMAT,
        options: this.sessionFormatOptions,
        formControl: this.filterFormGroup.controls.sessionFormats,
        isSearchable: false,
        isMulti: true
      },
      {
        category: FilterCategory.AGE_GROUP_TREATED,
        options: this.ageOptions,
        formControl: this.filterFormGroup.controls.ages,
        isSearchable: false,
        isMulti: true
      },
      {
        category: FilterCategory.LANGUAGE,
        options: this.languageOptions,
        formControl: this.filterFormGroup.controls.languages,
        isSearchable: true,
        isMulti: true
      },
      {
        category: FilterCategory.STATE,
        options: this.stateOptions,
        formControl: this.filterFormGroup.controls.state,
        isSearchable: true,
        isMulti: false
      }
    ];
  }

  updateFormValues(formValues: Partial<IFilterFormValues>): void {
    this.filterFormGroup.patchValue(formValues);
  }

  private updateMobileInsuranceFilter(): void {
    const insuranceFilterIndex = this.mobileMoreFilters.findIndex(
      (filterConfig) => filterConfig.category === FilterCategory.INSURANCE
    );
    if (insuranceFilterIndex !== -1) {
      this.mobileMoreFilters[insuranceFilterIndex] = {
        ...this.mobileMoreFilters[insuranceFilterIndex],
        options: this.insuranceOptions
      };
    }
  }

  private updateInsuranceOptions$() {
    return this.filterLoadingService.getInsuranceOptions$().pipe(
      map((insuranceOptions: Array<IOptionCategory<IInsuranceOptionValue>>) => {
        this.insuranceOptions = insuranceOptions;
        this.updateMobileInsuranceFilter();
        return this.filterFormGroup.value;
      })
    );
  }

  private subscribeToFilterLoadingError(): void {
    this.filterLoadingService.filterError$.pipe(
      takeUntil(this.destroyed$)
    ).subscribe((hasError) => {
      if (hasError) {
        this.filterRetrievalError.emit(true);
      }
    });
  }

  private subscribeToFilterValidation(): void {
    this.filterValidationService.setupFilterValidation$(this.route).pipe(
      takeUntil(this.destroyed$)
    ).subscribe();

    this.filterValidationService.invalidFilterState$.pipe(
      takeUntil(this.destroyed$),
      filter((x) => x),
      // Several validators can flip this in one tick; take(1) collapses them to a single redirect.
      take(1)
    ).subscribe(() => {
      this.redirectToNotFound.emit(true);
    });
  }

  private subscribeToBreakpointChanges(): void {
    this.breakpointService.isMobile$.pipe(
      takeUntil(this.destroyed$)
    ).subscribe((isMobile) => {
      this.isMobile.set(isMobile);
    });
  }

  updateFormWithPsychiatricOption(): void {
    const currentState = this.filterFormGroup.controls.state.value;
    const currentCity = this.filterFormGroup.controls.city.value;

    const psychiatryOption = this.modalityOptions[0]?.options?.find(
      (option) => option.value.title === PSYCHIATRIST_URL_TITLE
    )?.value;

    if (!psychiatryOption) return;

    const psychiatricFormValues: Partial<IFilterFormValues> = {
      state: currentState,
      city: currentCity,
      insurance: null,
      specialties: [],
      modalities: [psychiatryOption],
      genders: [],
      sessionFormats: [],
      ages: [],
      treatmentPreferences: [],
      languages: []
    };

    this.updateFormValues(psychiatricFormValues);
  }

  private subscribeToZipChanges(): void {
    this.filterFormGroup.controls.userZip.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      tap(() => {
        this.inPersonSearchStore.zipError.set(null);
        this.inPersonSearchStore.zipLoading.set(false);
      }),
      switchMap((zip) => {
        if (!zip || !/^\d{5}$/.test(zip)) {
          this.filterFormGroup.controls.userLatLon.setValue(null, { emitEvent: false });
          return EMPTY;
        }
        this.inPersonSearchStore.zipLoading.set(true);
        return this.geocodeService.forwardGeocode(zip).pipe(
          catchError(() => {
            this.inPersonSearchStore.zipError.set('Could not reach location service. Please try again.');
            return of(null);
          })
        );
      }),
      takeUntil(this.destroyed$)
    ).subscribe((latLon: ILatLon | null) => {
      this.inPersonSearchStore.zipLoading.set(false);
      this.filterFormGroup.controls.userLatLon.setValue(latLon, { emitEvent: false });

      // Do not overwrite a geocode outage error with "not found".
      if (latLon === null && !this.inPersonSearchStore.zipError()) {
        this.inPersonSearchStore.zipError.set('Zip code not found. Try another.');
      }

      // Always emit so a failed geocode clears stale results rather than leaving them beside the error.
      this.formFiltersChanged.emit(this.filterFormGroup.value);
    });
  }

  private subscribeToFormChanges(): void {
    this.filterFormGroup.valueChanges.pipe(
      filter(() => !this.isUpdatingForm),
      switchMap((values) => {
        if (!!values?.state && values.state !== this.filtersStore.getCurrentState()) {
          this.isUpdatingForm = true;
          this.filterFormGroup.patchValue({ city: EmptyURIValue, insurance: null });
          this.filtersStore.setCurrentState(values.state);
          this.isUpdatingForm = false;
          // Facet lists load off the state control itself (subscribeToFacetOptions), not this branch.
          return this.updateInsuranceOptions$();
        }
        const isPsychiatrist = values?.serviceType?.title === PSYCHIATRIST_URL_TITLE;
        const didIsPsychiatristChange = isPsychiatrist !== this.filtersStore.getCurrentIsPsychiatrist();
        if (didIsPsychiatristChange) {
          this.isUpdatingForm = true;
          this.filtersStore.setCurrentIsPsychiatrist(isPsychiatrist);
          return this.updateInsuranceOptions$().pipe(
            map(() => {
              const selectedInsurance = this.filterFormGroup.value.insurance;
              if (!isInsuranceInOptions(selectedInsurance, this.insuranceOptions)) {
                this.filterFormGroup.patchValue({ insurance: null });
              }
              this.isUpdatingForm = false;
              return this.filterFormGroup.value;
            })
          );
        }

        return of(values);
      }),
      takeUntil(this.destroyed$)
    ).subscribe((values) => {
      this.formFiltersChanged.emit(values);
    });
  }
}
